/**
 * Список покупок (запрос владельца 03.08.2026): «дома составил → у входа
 * отсканировал → обошёл по оптимальному кругу». Ключ позиции — id цели или
 * `{цель}::{товар}`; список живёт на устройстве (localStorage, per-venue) и
 * передаётся ссылкой `?list=` — без серверов и аккаунтов. Тур считает
 * planTour (@app/rvf): порядок остановок оптимизируется, финал — кассы.
 */
import type { BuiltGraph, RoutePlan, RouteStart, TourPlan, Venue } from '@app/rvf'
import { concatFloorLegs, planTour, splitByLevel } from '@app/rvf'
import { signal } from '@preact/signals'

/** Ключи позиций списка в порядке добавления. */
export const shoppingKeys = signal<readonly string[]>([])
/** Отмеченные «взял» ключи. */
export const shoppingChecked = signal<ReadonlySet<string>>(new Set())
/** Панель списка открыта. */
export const shoppingOpen = signal(false)
/** Построенный тур-обход (null — не строился/сброшен). */
export const shoppingTour = signal<ShoppingTour | null>(null)

/** Цель маршрута из ключа: 'shelf::milk' → 'shelf'. */
export function keyTargetId(key: string): string {
  const sep = key.indexOf('::')
  return sep === -1 ? key : key.slice(0, sep)
}

/** Товарная часть ключа; null — позиция без товара (полка/сервис целиком). */
export function keyProductId(key: string): string | null {
  const sep = key.indexOf('::')
  return sep === -1 ? null : key.slice(sep + 2)
}

/** `?list=` — ключи через запятую (id уже URL-безопасны: [\w-] и '::'). */
export function serializeList(keys: readonly string[]): string {
  return keys.join(',')
}

/** Разбор `?list=`: только существующие цели и товары, мусор отбрасывается. */
export function parseListParam(venue: Venue, raw: string): string[] {
  const products = new Map((venue.products ?? []).map((p) => [p.id, p]))
  const out: string[] = []
  for (const key of raw.split(',')) {
    const trimmed = key.trim()
    if (trimmed === '' || out.includes(trimmed)) continue
    const targetId = keyTargetId(trimmed)
    const productId = keyProductId(trimmed)
    const targetOk =
      venue.pois.some((p) => p.id === targetId) || venue.units.some((u) => u.id === targetId)
    if (!targetOk) continue
    if (productId !== null && products.get(productId)?.shelf !== targetId) continue
    out.push(trimmed)
  }
  return out
}

export interface ShoppingStop {
  readonly n: number
  readonly targetId: string
  /** Ключи списка, закрываемые этой остановкой (товары одной полки — вместе). */
  readonly keys: readonly string[]
  readonly point: readonly [number, number]
  readonly level: string
}

export interface ShoppingTour {
  readonly plan: TourPlan
  /** Склейка плеч в один план — для setRoute карты (линия всего обхода). */
  readonly composite: RoutePlan
  readonly stops: readonly ShoppingStop[]
}

/** Точка цели для маркера остановки. */
function targetPoint(venue: Venue, id: string): { point: [number, number]; level: string } | null {
  const poi = venue.pois.find((p) => p.id === id)
  if (poi) return { point: [poi.point[0], poi.point[1]], level: poi.level }
  const unit = venue.units.find((u) => u.id === id)
  const ring = unit?.geometry.coordinates[0]
  if (!unit || !ring || ring.length === 0) return null
  let lon = 0
  let lat = 0
  for (const c of ring) {
    lon += c[0] ?? 0
    lat += c[1] ?? 0
  }
  return { point: [lon / ring.length, lat / ring.length], level: unit.level }
}

/** Склейка планов плеч в один RoutePlan для отрисовки всего обхода линией. */
function compositePlan(venue: Venue, graph: BuiltGraph, tour: TourPlan): RoutePlan | null {
  const first = tour.stops[0]?.plan
  const last = tour.stops[tour.stops.length - 1]?.plan
  if (!first || !last) return null
  const concat = <T>(pick: (p: RoutePlan) => readonly T[]): T[] =>
    tour.stops.flatMap((s) => [...pick(s.plan)])
  const nodeIds = concat((p) => p.nodeIds)
  return {
    targetId: last.targetId,
    polyline: concat((p) => p.polyline),
    networkPolyline: concat((p) => p.networkPolyline),
    walkPolyline: concat((p) => p.walkPolyline),
    approach: first.approach,
    egress: last.egress,
    nodeIds,
    levelSplit: splitByLevel(graph, nodeIds),
    floorLegs: concatFloorLegs(
      tour.stops.map((s) => ({ polylineLength: s.plan.polyline.length, legs: s.plan.floorLegs })),
      (id) => venue.levels.find((l) => l.id === id)?.ordinal,
    ),
    distanceM: tour.distanceM,
    durationS: tour.durationS,
    profile: tour.profile,
    start: tour.start,
  }
}

/**
 * Тур по списку: цели дедуплицируются (товары одной полки — одна остановка),
 * финал — касса (если задана). null — пустой список или нет старта/сети.
 */
export function buildShoppingTour(
  venue: Venue,
  graph: BuiltGraph,
  start: RouteStart,
  keys: readonly string[],
  checkoutId: string | null,
): ShoppingTour | null {
  const byTarget = new Map<string, string[]>()
  for (const key of keys) {
    const target = keyTargetId(key)
    byTarget.set(target, [...(byTarget.get(target) ?? []), key])
  }
  const targets = [...byTarget.keys()]
  if (targets.length === 0) return null
  const withCheckout = checkoutId !== null && !byTarget.has(checkoutId)
  const plan = planTour(
    venue,
    graph,
    start,
    withCheckout ? [...targets, checkoutId] : targets,
    'foot',
    checkoutId !== null ? { finishId: checkoutId } : {},
  )
  if (!plan) return null
  const composite = compositePlan(venue, graph, plan)
  if (!composite) return null
  const stops: ShoppingStop[] = []
  plan.stops.forEach((stop, i) => {
    const at = targetPoint(venue, stop.targetId)
    if (!at) return
    stops.push({
      n: i + 1,
      targetId: stop.targetId,
      keys: byTarget.get(stop.targetId) ?? [],
      point: at.point,
      level: at.level,
    })
  })
  return { plan, composite, stops }
}

// ── Хранение на устройстве (per-venue) ───────────────────────────────────────

const storeKey = (venueId: string): string => `shopping:${venueId}`

interface StoredList {
  readonly keys: readonly string[]
  readonly checked: readonly string[]
}

/** Загрузка списка площадки; ?list= из ссылки сильнее сохранённого. */
export function loadShoppingList(venue: Venue, listParam: string | null): void {
  let keys: string[] = []
  let checked: string[] = []
  if (listParam !== null && listParam !== '') {
    keys = parseListParam(venue, listParam)
  } else {
    try {
      const raw = localStorage.getItem(storeKey(venue.venue.id))
      const parsed: unknown = raw === null ? null : JSON.parse(raw)
      if (parsed && typeof parsed === 'object') {
        const stored = parsed as StoredList
        keys = parseListParam(venue, serializeList(stored.keys ?? []))
        checked = (stored.checked ?? []).filter((k) => keys.includes(k))
      }
    } catch {
      // битое хранилище — начинаем с пустого списка
    }
  }
  shoppingKeys.value = keys
  shoppingChecked.value = new Set(checked)
}

function persist(venueId: string): void {
  try {
    const data: StoredList = {
      keys: shoppingKeys.value,
      checked: [...shoppingChecked.value],
    }
    localStorage.setItem(storeKey(venueId), JSON.stringify(data))
  } catch {
    // квота/приватный режим — список живёт до перезагрузки
  }
}

export function addToList(venueId: string, key: string): void {
  if (shoppingKeys.value.includes(key)) return
  shoppingKeys.value = [...shoppingKeys.value, key]
  shoppingTour.value = null
  persist(venueId)
}

export function removeFromList(venueId: string, key: string): void {
  shoppingKeys.value = shoppingKeys.value.filter((k) => k !== key)
  const next = new Set(shoppingChecked.value)
  next.delete(key)
  shoppingChecked.value = next
  shoppingTour.value = null
  persist(venueId)
}

export function toggleChecked(venueId: string, key: string): void {
  const next = new Set(shoppingChecked.value)
  if (next.has(key)) {
    next.delete(key)
  } else {
    next.add(key)
  }
  shoppingChecked.value = next
  persist(venueId)
}

export function clearList(venueId: string): void {
  shoppingKeys.value = []
  shoppingChecked.value = new Set()
  shoppingTour.value = null
  persist(venueId)
}
